/**
 * The visitor's country from CloudFront (`/geo.json` → `{ "country": "US" }`),
 * fetched once. Null on timeout, error, or when the edge function is absent
 * (the SPA then answers `/geo.json` with index.html, which is not JSON).
 */
let pending: Promise<string | null> | null = null;
let resolved: string | null = null;
let settled = false;

export function fetchViewerCountry(timeoutMs = 1500): Promise<string | null> {
  if (!pending) {
    pending = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch('/geo.json', { signal: controller.signal, cache: 'no-store' });
        if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return null;
        const body: unknown = await res.json();
        const country = (body as { country?: unknown } | null)?.country;
        resolved = typeof country === 'string' && /^[A-Z]{2}$/.test(country) ? country : null;
        return resolved;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
        settled = true;
      }
    })();
  }
  return pending;
}

/** The country once `fetchViewerCountry` has resolved; null before then. */
export function peekViewerCountry(): string | null {
  return resolved;
}

/** True once `fetchViewerCountry` has answered, with a country or without one. */
export function viewerCountrySettled(): boolean {
  return settled;
}

export function resetViewerCountryForTests(): void {
  pending = null;
  resolved = null;
  settled = false;
}
